import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, ExternalLink, Plus, Pencil, Trash2, Upload, Mail, Phone, X } from "lucide-react";
import Logo from "@/components/Logo";
import { api, imageUrl, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#E2C08D]/60 focus:outline-none transition-colors";

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd);
  return data.url;
}

function ImageField({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadImage(file));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err)); } finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-3">
      {value && <img src={imageUrl(value)} alt="" className="h-11 w-11 rounded-lg object-contain bg-white/95 border border-white/10" />}
      <input className={`${inputCls} flex-1`} placeholder="Image URL or upload →" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-[#E2C08D]/40 px-3 py-2.5 text-xs font-semibold text-[#E2C08D] hover:bg-[#E2C08D]/10 whitespace-nowrap" data-testid={`${testid}-upload`}>
        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
        <input type="file" accept="image/*" className="hidden" onChange={upload} />
      </label>
    </div>
  );
}

function ImageListField({ value = [], onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      onChange([...value, await uploadImage(file)]);
      toast.success("Photo added");
    } catch (err) { toast.error(formatApiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {value.map((url, i) => (
          <div key={i} className="relative group">
            <img src={imageUrl(url)} alt="" className="h-20 w-28 rounded-lg object-cover border border-white/10" />
            <button type="button" onClick={() => onChange(value.filter((_, x) => x !== i))} className="absolute -top-2 -right-2 rounded-full bg-rose-500 p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testid}-remove-${i}`} aria-label="Remove"><X size={11} /></button>
          </div>
        ))}
        <label className="flex h-20 w-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#E2C08D]/40 text-[#E2C08D] hover:bg-[#E2C08D]/5" data-testid={`${testid}-add`}>
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          <span className="text-[10px] font-medium">Add photo</span>
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
    </div>
  );
}

function EntityForm({ fields, initial, onSave, onClose, title }) {
  const [form, setForm] = useState(initial || {});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-[#0D1118] border-white/10 text-slate-200 max-h-[88vh] overflow-y-auto" data-testid="entity-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-slate-100">{title}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2 mt-2">
          {fields.map((f) => (
            <div key={f.key} className={["textarea", "list", "image", "imagelist"].includes(f.type) ? "sm:col-span-2" : ""}>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea className={`${inputCls} min-h-[110px]`} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`} />
              ) : f.type === "list" ? (
                <textarea className={inputCls} placeholder="One per line" value={(form[f.key] || []).join("\n")} onChange={(e) => set(f.key, e.target.value.split("\n").filter(Boolean))} data-testid={`field-${f.key}`} />
              ) : f.type === "select" ? (
                <select className={inputCls} value={form[f.key] || f.options[0]} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`}>
                  {f.options.map((o) => <option key={o} value={o} className="bg-[#10141d]">{o}</option>)}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm text-slate-300 pt-2">
                  <input type="checkbox" checked={!!form[f.key]} onChange={(e) => set(f.key, e.target.checked)} data-testid={`field-${f.key}`} className="accent-[#E2C08D] h-4 w-4" /> Yes
                </label>
              ) : f.type === "image" ? (
                <ImageField value={form[f.key] || ""} onChange={(v) => set(f.key, v)} testid={`field-${f.key}`} />
              ) : f.type === "imagelist" ? (
                <ImageListField value={form[f.key] || []} onChange={(v) => set(f.key, v)} testid={`field-${f.key}`} />
              ) : (
                <input className={inputCls} type={f.type === "number" ? "number" : "text"} value={form[f.key] ?? ""} onChange={(e) => set(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`field-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-slate-300 hover:text-white" data-testid="entity-cancel-button">Cancel</button>
          <button onClick={() => onSave(form)} className="rounded-full bg-[#E2C08D] px-6 py-2.5 text-sm font-semibold text-[#0A0C10] hover:bg-[#f0d3a4]" data-testid="entity-save-button">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CrudTab({ collection, fields, columns, title, subtitle }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/admin/${collection}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [collection]);

  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/admin/${collection}/${editing.id}`, form);
      else await api.post(`/admin/${collection}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${collection}/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid={`tab-content-${collection}`}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold text-slate-100">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <button onClick={() => setEditing({})} className="flex items-center gap-2 rounded-full bg-[#E2C08D] px-5 py-2.5 text-xs font-semibold text-[#0A0C10] hover:bg-[#f0d3a4]" data-testid={`add-${collection}-button`}>
          <Plus size={14} /> Add New
        </button>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-4 rounded-xl border border-white/8 bg-[#10141d] px-5 py-4" data-testid={`${collection}-row-${item.id.slice(0, 8)}`}>
            {(item.logo_url || item.url) && <img src={imageUrl(item.logo_url || item.url)} alt="" className="h-10 w-10 rounded-lg object-contain bg-white/95 border border-white/10 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-100 truncate">{columns.map((c) => item[c]).filter(Boolean).join(" — ")}</span>
                {item.status && <span className="rounded-full border border-[#E2C08D]/25 bg-[#E2C08D]/8 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[#E2C08D]">{item.status}</span>}
                {item.published === false && <span className="rounded-full border border-white/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">Draft</span>}
              </div>
              {(item.tagline || item.excerpt || item.caption) && <p className="mt-0.5 text-xs text-slate-500 truncate">{item.tagline || item.excerpt || item.caption}</p>}
            </div>
            <button onClick={() => setEditing(item)} className="text-slate-500 hover:text-[#E2C08D] p-2" data-testid={`edit-${collection}-${item.id.slice(0, 8)}`} aria-label="Edit"><Pencil size={15} /></button>
            <button onClick={() => remove(item.id)} className="text-slate-500 hover:text-rose-400 p-2" data-testid={`delete-${collection}-${item.id.slice(0, 8)}`} aria-label="Delete"><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-600 py-8 text-center">Nothing here yet — add the first one.</p>}
      </div>
      {editing !== null && <EntityForm fields={fields} initial={editing?.id ? editing : null} title={editing?.id ? `Edit ${title}` : `New ${title}`} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function QueriesTab() {
  const [queries, setQueries] = useState([]);
  const load = () => api.get("/admin/queries").then((r) => setQueries(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (q, status) => {
    await api.patch(`/admin/queries/${q.id}`, { status });
    load();
  };

  const typeColors = { investor: "text-[#38BDF8] border-[#38BDF8]/30 bg-[#38BDF8]/10", "business-owner": "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10", partner: "text-[#E2C08D] border-[#E2C08D]/30 bg-[#E2C08D]/10", fundraising: "text-[#FB7185] border-[#FB7185]/30 bg-[#FB7185]/10", general: "text-slate-300 border-white/15 bg-white/5" };

  return (
    <div data-testid="tab-content-queries">
      <h2 className="mb-6 font-display text-2xl font-semibold text-slate-100">Queries & Submissions</h2>
      <div className="space-y-3">
        {queries.map((q) => (
          <div key={q.id} className="rounded-xl border border-white/8 bg-[#10141d] p-5" data-testid={`query-row-${q.id.slice(0, 8)}`}>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="font-semibold text-slate-100">{q.name}</span>
              <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${typeColors[q.type] || typeColors.general}`}>{q.type.replace("-", " ")}</span>
              {q.related && <span className="text-xs text-slate-500">re: {q.related}</span>}
              {q.country && <span className="text-xs text-slate-500">· {q.country}</span>}
              <span className={`ml-auto rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${q.status === "new" ? "bg-[#E2C08D]/15 text-[#E2C08D]" : q.status === "follow-up" ? "bg-[#38BDF8]/10 text-[#38BDF8]" : "bg-white/5 text-slate-500"}`} data-testid={`query-status-${q.id.slice(0, 8)}`}>{q.status}</span>
            </div>
            {q.interests?.length > 0 && <p className="mt-2 text-xs text-slate-400">Interests: {q.interests.join(", ")}</p>}
            {q.message && <p className="mt-2 text-sm text-slate-300 leading-relaxed">“{q.message}”</p>}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-slate-300 hover:text-[#E2C08D] hover:border-[#E2C08D]/40" data-testid={`query-contact-${q.id.slice(0, 8)}`}><Mail size={12} /> {q.email}</a>
              {q.phone && <a href={`https://wa.me/${q.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-slate-300 hover:text-[#10B981] hover:border-[#10B981]/40"><Phone size={12} /> {q.phone}</a>}
              {q.status !== "follow-up" && <button onClick={() => setStatus(q, "follow-up")} className="rounded-full border border-[#38BDF8]/30 px-3.5 py-1.5 text-xs text-[#38BDF8] hover:bg-[#38BDF8]/10" data-testid={`query-followup-${q.id.slice(0, 8)}`}>Mark Follow-up</button>}
              {q.status !== "closed" && <button onClick={() => setStatus(q, "closed")} className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-slate-400 hover:text-slate-200" data-testid={`query-close-${q.id.slice(0, 8)}`}>Close</button>}
            </div>
          </div>
        ))}
        {queries.length === 0 && <p className="py-8 text-center text-sm text-slate-600" data-testid="queries-empty">No submissions yet. New queries from the website will appear here.</p>}
      </div>
    </div>
  );
}

function ProfileTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/profile").then((r) => setForm(r.data)).catch(() => {});
  }, []);
  if (!form) return <Loader2 className="animate-spin text-[#E2C08D]" size={24} />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/profile", form);
      toast.success("Profile saved — live on the website now.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="tab-content-profile">
      <h2 className="mb-2 font-display text-2xl font-semibold text-slate-100">Profile</h2>
      <p className="mb-8 text-xs text-slate-500">Your portrait appears in the Hero and About sections. Your biography replaces the default About text when filled.</p>
      <div className="space-y-5">
        <div>
          <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Your Photo (portrait)</label>
          <ImageField value={form.portrait_url || ""} onChange={(v) => setForm({ ...form, portrait_url: v })} testid="field-portrait" />
        </div>
        <div>
          <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Biography</label>
          <textarea className={`${inputCls} min-h-[140px]`} value={form.biography || ""} onChange={set("biography")} data-testid="field-biography" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</label>
            <input className={inputCls} value={form.email || ""} onChange={set("email")} data-testid="field-email" />
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">WhatsApp</label>
            <input className={inputCls} value={form.whatsapp || ""} onChange={set("whatsapp")} data-testid="field-whatsapp" />
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Instagram URL</label>
            <input className={inputCls} value={form.instagram || ""} onChange={set("instagram")} data-testid="field-instagram" />
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">LinkedIn URL</label>
            <input className={inputCls} value={form.linkedin || ""} onChange={set("linkedin")} data-testid="field-linkedin" />
          </div>
        </div>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-[#E2C08D] px-7 py-3 text-sm font-semibold text-[#0A0C10] hover:bg-[#f0d3a4] disabled:opacity-60" data-testid="profile-save-button">
          {saving && <Loader2 size={14} className="animate-spin" />} Save Profile
        </button>
      </div>
    </div>
  );
}

const TABS = [
  { id: "queries", label: "Queries" },
  { id: "ventures", label: "Companies & Projects" },
  { id: "fundraising", label: "Fundraising" },
  { id: "gallery", label: "Gallery" },
  { id: "journal", label: "Journal" },
  { id: "profile", label: "Profile" },
];

const VENTURE_FIELDS = [
  { key: "name", label: "Name" }, { key: "slug", label: "Slug (used in page URL)" },
  { key: "kind", label: "Type", type: "select", options: ["venture", "professional", "community"] },
  { key: "sector", label: "Sector" },
  { key: "status", label: "Status" }, { key: "role", label: "Your Role" },
  { key: "tagline", label: "Tagline" }, { key: "website", label: "Website" },
  { key: "linkedin", label: "LinkedIn" }, { key: "accent", label: "Accent", type: "select", options: ["gold", "sky", "green", "rose"] },
  { key: "order", label: "Sort Order", type: "number" }, { key: "featured", label: "Featured (large card)", type: "checkbox" },
  { key: "description", label: "Story / Description", type: "textarea" },
  { key: "highlights", label: "Highlights (one per line)", type: "list" },
  { key: "milestones", label: "Milestones / Journey So Far (one per line)", type: "list" },
  { key: "investors", label: "Investors (one per line)", type: "list" },
  { key: "team", label: "Team (one per line)", type: "list" },
  { key: "gallery", label: "Venture Gallery Photos", type: "imagelist" },
  { key: "logo_url", label: "Logo", type: "image" },
];

const FUNDRAISING_FIELDS = [
  { key: "company", label: "Company / Project" }, { key: "sector", label: "Sector" },
  { key: "stage", label: "Stage" }, { key: "capital", label: "Capital Required" },
  { key: "structure", label: "Investment Structure" }, { key: "status", label: "Status", type: "select", options: ["Open", "Closed", "Paused"] },
  { key: "order", label: "Sort Order", type: "number" },
  { key: "use_of_funds", label: "Use of Funds", type: "textarea" },
  { key: "traction", label: "Current Traction", type: "textarea" },
  { key: "opportunity", label: "Strategic Opportunity", type: "textarea" },
];

const GALLERY_FIELDS = [
  { key: "url", label: "Photo", type: "image" },
  { key: "category", label: "Category" }, { key: "caption", label: "Caption" },
  { key: "date", label: "Date" }, { key: "location", label: "Location" },
  { key: "featured", label: "Featured", type: "checkbox" },
];

const JOURNAL_FIELDS = [
  { key: "title", label: "Title" }, { key: "category", label: "Category" },
  { key: "read_time", label: "Read Time" }, { key: "published", label: "Published", type: "checkbox" },
  { key: "excerpt", label: "Excerpt", type: "textarea" },
  { key: "content", label: "Full Article", type: "textarea" },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("queries");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin/login"));
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (tab === "queries") api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [tab]);

  if (!user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#E2C08D]" size={28} /></div>;

  const statCards = stats ? [
    ["Total Queries", stats.queries_total], ["New Today", stats.queries_today], ["Awaiting Reply", stats.queries_new],
    ["Investor", stats.investor_enquiries], ["Business Owners", stats.business_owner_enquiries], ["Partnerships", stats.partnership_requests],
    ["Fundraising", stats.fundraising_enquiries], ["Ventures", stats.ventures], ["Gallery", stats.gallery_items], ["Journal", stats.journal_posts],
  ] : [];

  return (
    <div className="min-h-screen bg-[#0A0C10]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 glass border-b border-white/8">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-8 h-16">
          <div className="flex items-center gap-3">
            <Logo size={32} />
            <div className="leading-tight">
              <div className="font-display text-base font-semibold text-slate-100">Bishwa Mohan — Admin</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E2C08D]/80">Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs text-slate-300 hover:text-[#E2C08D]" data-testid="admin-view-site"><ExternalLink size={12} /> View Site</Link>
            <button onClick={async () => { await api.post("/auth/logout"); navigate("/admin/login"); }} className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs text-slate-300 hover:text-rose-400" data-testid="admin-logout-button"><LogOut size={12} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 md:px-8 py-10">
        {stats && (
          <div className="mb-10 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="admin-stats">
            {statCards.map(([label, val]) => (
              <div key={label} className="rounded-xl border border-white/8 bg-[#10141d] px-4 py-4">
                <div className="font-display text-2xl font-semibold text-[#E2C08D]" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{val}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mb-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${tab === t.id ? "border-[#E2C08D] bg-[#E2C08D]/15 text-[#E2C08D]" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "queries" && <QueriesTab />}
        {tab === "ventures" && <CrudTab collection="ventures" fields={VENTURE_FIELDS} columns={["name"]} title="Companies & Projects" subtitle="Every card and its full detail page is controlled from here." />}
        {tab === "fundraising" && <CrudTab collection="fundraising" fields={FUNDRAISING_FIELDS} columns={["company", "stage"]} title="Fundraising Opportunities" />}
        {tab === "gallery" && <CrudTab collection="gallery" fields={GALLERY_FIELDS} columns={["caption", "category"]} title="Gallery — Moments That Built the Journey" subtitle="Upload real photos from your journey — they appear on the site instantly." />}
        {tab === "journal" && <CrudTab collection="journal" fields={JOURNAL_FIELDS} columns={["title"]} title="Journal — Ideas, Journeys & Conversations" />}
        {tab === "profile" && <ProfileTab />}
      </div>
    </div>
  );
}
